import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Handshake, MapPin, Tag, Truck, Users, Lightbulb,
  EnvelopeSimple, Info, ShieldCheck, ArrowRight,
} from "@phosphor-icons/react";

const TIPS = [
  {
    icon: MapPin,
    title: "Lokal denken",
    body: "Schau zuerst im Umkreis von 10–15 km. Transport-Kosten fressen schnell den Preisvorteil weg, vor allem bei Stroh und Gülle.",
  },
  {
    icon: Tag,
    title: "Klare Mengen, klare Qualität",
    body: '„30 Rundballen Heu, 1. Schnitt 2026, trocken eingebracht" sagt mehr als „Heu zu verkaufen". Je präziser, desto schneller findest du Abnehmer.',
  },
  {
    icon: Truck,
    title: "Logistik vorher klären",
    body: "Wer holt ab? Wer lädt? Bezahlung bar oder Rechnung? Diese drei Punkte vorher ausmachen — vermeidet 95 % aller Konflikte.",
  },
  {
    icon: Users,
    title: "Tausch statt Verkauf",
    body: "Stroh gegen Mist, Maschinen-Stunden gegen Heu. Klassisch, papierfrei, gut fürs Bauern-Karma.",
  },
  {
    icon: Lightbulb,
    title: "Reststoffe nutzen",
    body: "Was du nicht brauchst, kann der Nachbar dringend wollen. Ein Blühstreifen-Saatgutrest, eine halbe Big-Bag Saatkartoffeln — frag einfach nach.",
  },
  {
    icon: ShieldCheck,
    title: "Sicher kommunizieren",
    body: "Erste Kontaktaufnahme idealerweise über Genossenschaft, LK-Bezirksstelle oder bekannte Vermittler. Bei Privat-Deals immer schriftlich bestätigen.",
  },
];

const PLACES = [
  {
    name: "AMA Marktbericht (AT)",
    url: "https://www.ama.at/marktinformationen",
    desc: "Wöchentliche Erfasser-Preise für Getreide, Vieh, Gemüse — Pflicht-Lektüre vor jedem Verkauf.",
  },
  {
    name: "AMI Markt + Information (DE)",
    url: "https://www.ami-informiert.de/",
    desc: "Pendant für Deutschland — täglich aktualisierte Erzeugerpreise.",
  },
  {
    name: "Landwirt.com Tauschbörse",
    url: "https://www.landwirt.com/agrar-tauschboerse/",
    desc: "Größte deutschsprachige Tauschplattform für Land- und Forstwirtschaft. Maschinen, Heu, Saatgut.",
  },
  {
    name: "Lokale Genossenschaft",
    url: "#",
    desc: "Raiffeisen, RWA, Lagerhaus — die kennen oft Ab- und Verkäufer:innen in deinem Umkreis besser als jede Online-Plattform.",
  },
  {
    name: "Facebook-Gruppen pro Bezirk",
    url: "https://www.facebook.com/groups/",
    desc: 'Such „Bauern [Bezirk]" — viele Bezirke haben aktive geschlossene Gruppen mit Tausch-Posts.',
  },
];

export default function Marketplace() {
  return (
    <div className="space-y-6" data-testid="marketplace-page">
      <Helmet>
        <title>Erntepartner-Marktplatz · bodenblick.com</title>
      </Helmet>

      <div
        className="eid-card p-6 md:p-10 relative overflow-hidden"
        data-testid="market-hero"
        style={{
          background:
            "radial-gradient(circle at 88% 18%, #0E1A14 0%, transparent 55%), linear-gradient(135deg, #1A160A 0%, #181818 100%)",
        }}
      >
        <div className="relative">
          <div className="flex items-center gap-2">
            <Handshake size={22} className="text-[#00FA9A]" weight="fill" />
            <div className="eid-label">Erntepartner-Marktplatz · Info</div>
          </div>
          <h1 className="mt-3 text-3xl md:text-5xl font-medium tracking-tight max-w-3xl">
            Lokale Tauschwirtschaft schlägt Welt-Markt.
          </h1>
          <p className="mt-4 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
            Heu im Überschuss, dem Nachbarn fehlt's. Stroh aus der ersten Tracht
            zum Mistlager nebenan. Eine Sämaschine 3 Tage frei. Ein lokaler
            Erntepartner-Markt holt aus dem, was übrig bleibt, neue Wertschöpfung.
            Hier findest du Tipps, wie du das selbst aufbaust — analog oder online.
          </p>
        </div>
      </div>

      {/* Tipps */}
      <section data-testid="market-tips">
        <h2 className="eid-label mb-3">6 goldene Regeln</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {TIPS.map((t, i) => {
            const Icon = t.icon;
            return (
              <div
                key={i}
                data-testid={`market-tip-${i}`}
                className="eid-card p-5"
              >
                <span className="p-2 rounded-sm bg-[#00FA9A]/10 text-[#00FA9A] inline-block">
                  <Icon size={16} weight="fill" />
                </span>
                <h3 className="mt-3 font-medium">{t.title}</h3>
                <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{t.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Wo finde ich was? */}
      <section className="eid-card p-5" data-testid="market-places">
        <h2 className="text-base font-medium flex items-center gap-2">
          <MapPin size={18} className="text-[#00FA9A]" weight="fill" />
          Wo finde ich Tausch- und Verkaufs-Möglichkeiten?
        </h2>
        <ul className="mt-3 space-y-3">
          {PLACES.map((p, i) => (
            <li
              key={i}
              data-testid={`market-place-${i}`}
              className="border-l-2 border-[#00FA9A]/40 pl-3"
            >
              {p.url === "#" ? (
                <div className="font-medium">{p.name}</div>
              ) : (
                <a
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-[#00FA9A] hover:underline inline-flex items-center gap-1"
                >
                  {p.name} <ArrowRight size={11} />
                </a>
              )}
              <div className="text-sm text-zinc-400 mt-0.5">{p.desc}</div>
            </li>
          ))}
        </ul>
      </section>

      {/* Hinweis */}
      <div className="eid-card p-4 text-xs text-zinc-400 flex items-start gap-2" data-testid="market-info-note">
        <Info size={14} className="text-[#00FA9A] shrink-0 mt-0.5" />
        <span>
          <strong>Bodenblick selbst</strong> betreibt aktuell keinen eigenen
          Erntepartner-Marktplatz mit User-Inseraten. Das ist eine bewusste
          Entscheidung: ein anonymer Marktplatz mit fremden Kontaktdaten
          braucht eine saubere DSGVO-Auftragsverarbeitung, die wir aktuell
          nicht in Europa anbieten können. Stattdessen verlinken wir hier auf
          etablierte Plattformen, die das rechtssicher tun.
        </span>
      </div>

      {/* Inspiration footer */}
      <div
        className="eid-card p-5"
        style={{ background: "linear-gradient(135deg, #0E1A14 0%, #141414 100%)" }}
      >
        <h2 className="text-base font-medium">Eigene Genossenschaft starten?</h2>
        <p className="text-sm text-zinc-400 mt-1">
          Manchmal reicht eine WhatsApp-Gruppe für 5 Höfe in deiner Gegend.
          Größer skaliert: Maschinenring (über 100.000 Mitglieder in DE/AT) —
          Maschinen-Sharing, Arbeits-Tausch, Notfall-Hilfe. Kostet wenig, bringt viel.
        </p>
        <a
          href="https://www.maschinenring.at"
          target="_blank"
          rel="noreferrer"
          data-testid="market-mr-link"
          className="mt-3 inline-flex items-center gap-1 text-[#00FA9A] hover:underline text-sm"
        >
          Maschinenring entdecken <ArrowRight size={12} />
        </a>
      </div>
    </div>
  );
}
